import { useQuery } from '@tanstack/react-query'
import { EngagementTile, type EngagementTileData } from '@/components/contacts/EngagementTile'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON } from '@/lib/api'
import { engagementHref } from './organizationEngagement'
import { pushLocation } from './timelineFilters'

interface EngagementHeadline {
  label: string
  window_label: string
  lapsed_banner: string | null
  empty_message: string | null
  tiles: EngagementTileData[]
}

export function OrganizationEngagementPanel({
  contactId,
  onAdd,
  canManageAffiliations,
}: {
  contactId: number
  onAdd: () => void
  canManageAffiliations: boolean
}) {
  const query = useQuery({
    queryKey: ['organization-engagement', contactId],
    queryFn: () =>
      getJSON<{ headline: EngagementHeadline | null }>(`/api/v1/contacts/${contactId}/engagement`),
  })
  if (query.isPending) return <EmptyState title="Loading engagement" variant="quiet" />
  if (query.isError) return <Alert variant="destructive">Engagement unavailable</Alert>
  const headline = query.data.headline
  if (!headline) return null
  return (
    <Card role="region" aria-label={headline.label}>
      <div className="flex flex-wrap items-baseline gap-x-2 border-b border-border px-4 py-3">
        <h2 className="text-body font-medium text-foreground">{headline.label}</h2>
        <span className="text-caption text-muted-foreground">{headline.window_label}</span>
        <a
          className="ml-auto text-caption text-primary hover:underline"
          href={engagementHref()}
          onClick={(event) => {
            event.preventDefault()
            pushLocation(new URL(event.currentTarget.href))
          }}
        >
          View all
        </a>
      </div>
      {headline.lapsed_banner && (
        <div className="border-b border-border px-4 py-2">
          <Alert variant="warning">{headline.lapsed_banner}</Alert>
        </div>
      )}
      {headline.empty_message && (
        <EmptyState
          title={headline.empty_message}
          action={
            canManageAffiliations ? (
              <Button type="button" variant="outline" onClick={onAdd}>
                + Add person
              </Button>
            ) : undefined
          }
        />
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5">
        {headline.tiles.map((tile) => (
          <a
            key={tile.key}
            href={engagementHref(tile.key, tile.key === 'roster' ? 'membership' : 'engagement')}
            className="border-border hover:bg-accent even:border-l sm:[&:not(:first-child)]:border-l"
            onClick={(event) => {
              event.preventDefault()
              pushLocation(new URL(event.currentTarget.href))
            }}
          >
            <EngagementTile tile={tile} />
          </a>
        ))}
      </div>
    </Card>
  )
}
